/**
 * PINCH AND PAN, AS ARITHMETIC — M10 / S10-03B.
 *
 * **THE GESTURE LAYER IS THE LAST PRESENTATION LAYER, AND IT DECIDES NOTHING
 * ABOUT THE REPORT.** Sections are measured at canonical Letter width, packed
 * into canonical pages, then fitted to the phone, and only then may Brandon
 * pinch. Nothing in this file can move a page break, change a page count or
 * touch a photo — it moves a picture of a document around a window.
 *
 * **PURE, BECAUSE TOUCH IS THE HARDEST THING TO TEST BY HAND.** Clamping, the
 * distance ratio and the pan bounds are ordinary numbers here, so "he cannot
 * lose the document off screen" is a unit test rather than something discovered
 * on a phone in a basement.
 */

/** Fit-width is the floor: pinching all the way in returns to the whole page. */
export const MIN_ZOOM = 1;

/**
 * Enough to read contact details and inspect a photo, and no more.
 *
 * Four times fit-width puts a Letter page at roughly its true printed size on a
 * phone, which is the point at which further zoom stops telling Brandon
 * anything about the document he is about to send.
 */
export const MAX_ZOOM = 4;

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Distance between two fingers. The pinch ratio is one of these over another. */
export function pinchDistance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export interface ZoomState {
  readonly zoom: number;
  readonly pan: Point;
}

/**
 * Keep the document reachable.
 *
 * **AT FIT-WIDTH THERE IS NOTHING TO PAN SIDEWAYS TO**, so horizontal movement
 * is pinned to zero — otherwise a stray thumb slides the page off the screen
 * and it looks broken. Vertically the surface is a window onto a stack that is
 * usually taller than it, so panning is how Brandon moves between pages, and it
 * is bounded to the stack's own ends.
 */
export function clampPan(
  pan: Point,
  options: {
    readonly zoom: number;
    readonly contentWidth: number;
    readonly contentHeight: number;
    readonly viewportWidth: number;
    readonly viewportHeight: number;
  },
): Point {
  const scaledWidth = options.contentWidth * options.zoom;
  const scaledHeight = options.contentHeight * options.zoom;

  // Overflow is what there is to pan across. No overflow, no panning.
  const slackX = Math.max(0, scaledWidth - options.viewportWidth);
  const slackY = Math.max(0, scaledHeight - options.viewportHeight);

  // `|| 0` normalises negative zero, which `Math.max(-0, …)` produces and which
  // would otherwise reach a style string as `translate(-0px, -0px)`.
  return {
    x: Math.min(0, Math.max(-slackX, pan.x)) || 0,
    y: Math.min(0, Math.max(-slackY, pan.y)) || 0,
  };
}

/**
 * Zoom about the point between Brandon's fingers, so the thing he is looking at
 * stays under them. Zooming about a corner instead makes the content appear to
 * flee the gesture.
 */
export function zoomAbout(
  state: ZoomState,
  focus: Point,
  nextZoom: number,
  bounds: {
    readonly contentWidth: number;
    readonly contentHeight: number;
    readonly viewportWidth: number;
    readonly viewportHeight: number;
  },
): ZoomState {
  const zoom = clampZoom(nextZoom);
  const ratio = zoom / state.zoom;

  // The document point under the fingers before the change must remain under
  // them after it.
  const pan = {
    x: focus.x - (focus.x - state.pan.x) * ratio,
    y: focus.y - (focus.y - state.pan.y) * ratio,
  };

  return { zoom, pan: clampPan(pan, { zoom, ...bounds }) };
}

/** Fit-width again: nothing is zoomed, so nothing is offset. */
export const RESET_ZOOM: ZoomState = { zoom: MIN_ZOOM, pan: { x: 0, y: 0 } };
